import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { isEqual, pick } from 'lodash';
import { withTranslation } from 'react-i18next';

import { Checkbox, SelectSimple, FormField } from '@mssgme/ui';

import styles from './styles.scss';

const currenciesArray = [
    'USD',
    'EUR',
    'RUB',
    'UAH',
    'AED',
    'AFN',
    'ALL',
    'AMD',
    'ANG',
    'AOA',
    'ARS',
    'AUD',
    'AWG',
    'AZN',
    'BAM',
    'BBD',
    'BDT',
    'BGN',
    'BHD',
    'BIF',
    'BMD',
    'BND',
    'BOB',
    'BOV',
    'BRL',
    'BSD',
    'BTN',
    'BWP',
    'BYN',
    'BZD',
    'CAD',
    'CDF',
    'CHE',
    'CHF',
    'CHW',
    'CLF',
    'CLP',
    'CNH',
    'CNY',
    'COP',
    'COU',
    'CRC',
    'CUC',
    'CUP',
    'CVE',
    'CZK',
    'DJF',
    'DKK',
    'DOP',
    'DZD',
    'EGP',
    'ERN',
    'ETB',
    'FJD',
    'FKP',
    'GBP',
    'GEL',
    'GHS',
    'GIP',
    'GMD',
    'GNF',
    'GTQ',
    'GYD',
    'HKD',
    'HNL',
    'HRK',
    'HTG',
    'HUF',
    'IDR',
    'ILS',
    'INR',
    'IQD',
    'IRR',
    'ISK',
    'JMD',
    'JOD',
    'JPY',
    'KES',
    'KGS',
    'KHR',
    'KMF',
    'KPW',
    'KRW',
    'KWD',
    'KYD',
    'KZT',
    'LAK',
    'LBP',
    'LKR',
    'LRD',
    'LSL',
    'LYD',
    'MAD',
    'MDL',
    'MGA',
    'MKD',
    'MMK',
    'MNT',
    'MOP',
    'MRO',
    'MUR',
    'MWK',
    'MXN',
    'MXV',
    'MYR',
    'MZN',
    'NAD',
    'NGN',
    'NIO',
    'NOK',
    'NPR',
    'NZD',
    'OMR',
    'PAB',
    'PEN',
    'PGK',
    'PHP',
    'PKR',
    'PLN',
    'PYG',
    'QAR',
    'RON',
    'RSD',
    'RWF',
    'SAR',
    'SBD',
    'SCR',
    'SDG',
    'SEK',
    'SGD',
    'SHP',
    'SLL',
    'SOS',
    'SRD',
    'SSP',
    'STN',
    'SYP',
    'SZL',
    'THB',
    'TJS',
    'TND',
    'TOP',
    'TRY',
    'TTD',
    'TWD',
    'TZS',
    'UGX',
    'USN',
    'UYI',
    'UYU',
    'UZS',
    'VEF',
    'VND',
    'VUV',
    'WST',
    'XAF',
    'XCD',
    'XOF',
    'XPF',
    'YER',
    'ZAR',
    'ZMW',
];
const currenciesObjectsArray = currenciesArray.map((item) => ({ label: item, value: item }));

const strToFloat = (str) => (str === '' ? null : parseFloat(str));
const normalizePrice = ({ amount, discount, currency }) => ({
    amount: strToFloat(amount),
    discount: strToFloat(discount),
    currency,
});

const initState = (value, evalDiscount) => ({
    ...(evalDiscount && { hasDiscount: !!value.discount }),
    value: {
        ...value,
        amount: value.amount == null ? '' : String(value.amount),
        discount: value.discount == null ? '' : String(value.discount),
    },
    normalizedValue: { ...value },
});

class ProductPriceInputs extends Component {
    static propTypes = {
        value: PropTypes.shape({
            amount: PropTypes.number,
            currency: PropTypes.string.isRequired,
            discount: PropTypes.number,
        }),
        refAmount: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
        name: PropTypes.string,
        onChange: PropTypes.func,
        errors: PropTypes.object,
        t: PropTypes.func,
    };

    static getDerivedStateFromProps(props, state) {
        const value = pick(props.value, ['amount', 'currency', 'discount']);

        if (!isEqual(value, state.prevValue)) {
            return {
                ...initState(props.value),
                prevValue: value,
            };
        }

        return null;
    }

    state = initState(this.props.value, true);

    handleChange = ({ target: { name, value } }) => {
        if (name === 'amount' || name === 'discount') {
            value = value.replace(',', '.').trim();

            if (value !== '') {
                const isValid = value.match(/^\d{0,9}(\d\.\d{0,2})?$/);

                if (!isValid) return;
            }
        }

        this.setState(
            (state) => {
                const newValue = {
                    ...state.value,
                    [name]: value,
                };

                return {
                    value: newValue,
                    normalizedValue: normalizePrice(newValue),
                };
            },
            () => {
                if (this.props.onChange && !isEqual(this.props.value, this.state.normalizedValue)) {
                    this.props.onChange({
                        target: {
                            name: this.props.name,
                            value: this.state.normalizedValue,
                        },
                    });
                }
            }
        );
    };

    toggleDiscount = () => {
        this.setState(
            (state) => {
                const newValue = {
                    ...state.value,
                    discount: '',
                };

                return {
                    hasDiscount: !state.hasDiscount,
                    value: newValue,
                    normalizedValue: normalizePrice(newValue),
                };
            },
            () => {
                if (this.props.onChange && !isEqual(this.props.value, this.state.normalizedValue)) {
                    this.props.onChange({
                        target: {
                            name: this.props.name,
                            value: this.state.normalizedValue,
                        },
                    });
                }
            }
        );
    };

    render() {
        const { errors, refAmount, t } = this.props;
        const {
            value: { amount, discount, currency },
            hasDiscount,
        } = this.state;

        return (
            <div className={styles.root}>
                <div className={styles.priceContainer}>
                    <div className={styles.priceInput}>
                        <FormField
                            name="amount"
                            value={amount}
                            // placeholder={'Enter product price'}
                            onChange={this.handleChange}
                            type="text"
                            errors={errors && errors.amount && t(errors.amount)}
                            inputMode="decimal"
                            refInput={refAmount}
                        />
                    </div>
                    <div className={styles.currencySelect}>
                        <FormField
                            component={SelectSimple}
                            options={currenciesObjectsArray}
                            name="currency"
                            value={currency}
                            onChange={this.handleChange}
                            errors={errors && errors.currency && t(errors.currency)}
                        />
                    </div>
                </div>
                {hasDiscount && (
                    <FormField
                        name="discount"
                        value={discount}
                        label={t('productEdit.discountLabel')}
                        // placeholder={'Enter product price'}
                        onChange={this.handleChange}
                        type="text"
                        errors={errors && errors.discount && t(errors.discount)}
                        inputMode="decimal"
                    />
                )}
                <div className={classNames(styles.publishedContainer, styles.priceTogglerContainer)}>
                    <div className={classNames(styles.publishedLabel, styles.priceLabel)}>
                        <span>{t('productEdit.hasDiscount')}</span>
                    </div>
                    <div>
                        <Checkbox checked={hasDiscount} onChange={this.toggleDiscount} styled="toggle" />
                    </div>
                </div>
            </div>
        );
    }
}

export default withTranslation('cards')(ProductPriceInputs);
